import type { ReactNode } from "react";
import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAlert } from "../app/notificationToast";
import { useConfirmationModal } from "../app/confirmation";
import { MobileDeviceAdapter } from "../../api/mobileDevices/MobileDeviceAdapter";
import { MobileDeviceModel } from "../../api/mobileDevices/MobileDeviceModel";
import type {
  InitiatePairingRequest,
  MobileDevice,
  MobilePairingInitiation,
} from "../../api/mobileDevices/MobileDevice.typed";

interface MobileDevicesContextType {
  initiatePairing: (payload?: InitiatePairingRequest) => Promise<MobilePairingInitiation | null>;
  restartPairing: (deviceId: number) => Promise<MobilePairingInitiation | null>;
  listDevices: () => Promise<MobileDevice[]>;
  deleteDevice: (device: MobileDevice) => Promise<boolean>;
}

const MobileDevicesContext = React.createContext<MobileDevicesContextType>(null!);

export function MobileDevicesProvider(props: { children: ReactNode }) {
  const appConfig = React.useContext(AppConfigContext);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const adapter = React.useMemo(() => {
    if (!appConfig) return null;

    return new MobileDeviceAdapter(new MobileDeviceModel(appConfig));
  }, [appConfig]);

  const initiatePairing = React.useCallback(
    async (payload?: InitiatePairingRequest): Promise<MobilePairingInitiation | null> => {
      if (!adapter) return null;

      try {
        return await adapter.initiatePairing(payload);
      } catch (err) {
        showAlert(
          err instanceof Error ? err.message : "Échec de l'initiation d'appairage",
          "danger",
        );

        return null;
      }
    },
    [adapter, showAlert],
  );

  const restartPairing = React.useCallback(
    async (deviceId: number): Promise<MobilePairingInitiation | null> => {
      if (!adapter) return null;

      try {
        return await adapter.restartPairing(deviceId);
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Échec du réappairage", "danger");

        return null;
      }
    },
    [adapter, showAlert],
  );

  const listDevices = React.useCallback(async (): Promise<MobileDevice[]> => {
    if (!adapter) return [];

    try {
      return await adapter.listDevices();
    } catch (err) {
      showAlert(
        err instanceof Error ? err.message : "Échec de la récupération des appareils",
        "danger",
      );

      return [];
    }
  }, [adapter, showAlert]);

  const deleteDevice = React.useCallback(
    async (device: MobileDevice): Promise<boolean> => {
      if (!adapter) return false;

      const ok = await showConfirmation(
        "Supprimer l'appareil",
        `Cette action supprime définitivement "${device.label}" et invalide son accès. Continuer ?`,
      );

      if (!ok) return false;

      try {
        await adapter.deleteDevice(device.id);
        showAlert("Appareil supprimé", "success");

        return true;
      } catch (err) {
        showAlert(err instanceof Error ? err.message : "Échec de la suppression", "danger");

        return false;
      }
    },
    [adapter, showAlert, showConfirmation],
  );

  const value = React.useMemo<MobileDevicesContextType>(
    () => ({ initiatePairing, restartPairing, listDevices, deleteDevice }),
    [initiatePairing, restartPairing, listDevices, deleteDevice],
  );

  return (
    <MobileDevicesContext.Provider value={value}>{props.children}</MobileDevicesContext.Provider>
  );
}

export function useMobileDevices(): MobileDevicesContextType {
  const ctx = React.useContext(MobileDevicesContext);

  if (!ctx) {
    throw new Error("useMobileDevices must be used within MobileDevicesProvider");
  }

  return ctx;
}
